import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Loading, EmptyState } from "../components/Common";
import PremiumReport from "../components/PremiumReport";
import { Printer, FileText, Download, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ReportCards() {
  const [students, setStudents] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    if (!selected) return;
    setDownloading(true);
    try {
      const { data: blob } = await api.premiumReportPdfBlob(selected);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const name = `${report?.student?.first_name || "student"}-${report?.student?.last_name || ""}-report.pdf`.toLowerCase().replace(/\s+/g, "-");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Executive report downloaded");
    } catch (e) {
      toast.error("Unable to download executive report");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    api.students().then(({ data }) => {
      setStudents(data);
      // Auto-select Edwin Thom if present (now guaranteed reachable since we
      // fetch the full canonical population, not the first 500 records).
      const edwin = data.find((s) => s.first_name === "Edwin" && s.last_name === "Thom");
      if (edwin) {
        setSelected(edwin.id);
        // Bias search so the left rail scrolls Edwin into view without wiping
        // the list — the search field is a soft filter that narrows to matches.
        setQ("Edwin");
      } else if (data.length > 0) {
        setSelected(data[0].id);
      }
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setReport(null);
    api
      .premiumReport(selected)
      .then(({ data }) => setReport(data))
      .catch(() => toast.error("Unable to load report"))
      .finally(() => setLoading(false));
  }, [selected]);

  if (students === null) return <Loading />;

  const filtered = q
    ? students.filter((s) => `${s.first_name} ${s.last_name} ${s.admission_no}`.toLowerCase().includes(q.toLowerCase()))
    : students;

  return (
    <div data-testid="reports-page">
      <PageHeader
        kicker="International Executive Report"
        title="Termly Student Report"
        subtitle="Publication-quality academic report benchmarked against premium private schools in the UK, Singapore, Australia and the IB circuit."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="hl-btn-primary inline-flex items-center gap-2 text-sm"
              data-testid="btn-print"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
            <button
              onClick={downloadPdf}
              disabled={!selected || downloading}
              className="hl-btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-60"
              data-testid="btn-download-pdf"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {downloading ? "Composing…" : "Download PDF"}
            </button>
          </div>
        }
      />

      <div className="grid md:grid-cols-12 gap-6 print:block">
        {/* Student selector — hidden on print */}
        <aside className="md:col-span-3 print:hidden" data-testid="report-selector">
          <div className="hl-card p-3">
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search students…"
                className="w-full pl-8 pr-2 py-1.5 text-xs border border-slate-200 rounded-md"
                data-testid="report-search"
              />
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {filtered.slice(0, 80).map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelected(s.id)}
                  data-testid={`report-select-${s.id}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${selected === s.id ? "bg-[color:var(--hl-orange-soft)] text-[color:var(--hl-orange)] font-semibold" : "hover:bg-slate-50"}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{s.first_name} {s.last_name}</span>
                    {s.first_name === "Edwin" && s.last_name === "Thom" && (
                      <span className="text-[9px] uppercase tracking-widest text-[color:var(--hl-orange)]">Demo</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500">{s.year_group} · {s.house}</div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Report view */}
        <div className="md:col-span-9 print:col-span-12">
          {!selected && (
            <EmptyState icon={FileText} title="Select a student" body="Choose a student to preview the executive report." />
          )}
          {loading && <Loading label="Composing executive report…" />}
          {report && <PremiumReport data={report} />}
        </div>
      </div>
    </div>
  );
}
